import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';
import { modeloContratoService, ModeloContrato } from '../../../../services/contratoService';
import { formatDateFromDB } from '../../../../utils/dateUtils';

interface ModelosContratoPageProps {
  onEdit: (id: string) => void;
  onNew: () => void;
}

export function ModelosContratoPage({ onEdit, onNew }: ModelosContratoPageProps) {
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarModelos();
  }, []);

  async function carregarModelos() {
    try {
      setLoading(true);
      const data = await modeloContratoService.list();
      setModelos(data);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Tem certeza que deseja excluir este modelo?')) return;

    try {
      await modeloContratoService.delete(id);
      carregarModelos();
    } catch (error) {
      console.error('Erro ao excluir modelo:', error);
      alert('Erro ao excluir modelo');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Modelos de Contrato</h2>
          <p className="text-muted-foreground">Blocos de texto reutilizáveis para composição de contratos</p>
        </div>
        <button
          onClick={onNew}
          className="btn btn-primary btn-md"
        >
          <Plus className="w-4 h-4" />
          Novo Modelo
        </button>
      </div>

      {/* Lista */}
      {modelos.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg shadow-sm">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Nenhum modelo encontrado</h3>
          <p className="text-muted-foreground mb-4">Crie seu primeiro modelo de contrato</p>
          <button
            onClick={onNew}
            className="btn btn-secondary btn-md"
          >
            Criar Modelo
          </button>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tipo de negócio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Criado em
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {modelos.map((modelo) => (
                <tr key={modelo.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-muted-foreground mr-3" />
                      <div>
                        <span className="font-medium text-foreground">{modelo.titulo}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {modelo.tipoNegocio || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDateFromDB(modelo.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => onEdit(modelo.id)}
                      className="text-primary hover:text-primary/80 mr-3"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(modelo.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}
